import { useState, lazy, Suspense } from 'react';
import { Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useUserData, { UserType, useUserArticlesQuery } from 'hooks/useUserData';
import Loader from 'components/common/loader/Loader';
import ArticleBox from 'pages/knowledge/components/ArticleBox';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import BuildingFillIcon from 'remixicon-react/BuildingFillIcon';
import HandleOperation from 'components/utils/HandleOperation';
import ProjectSmallBox from '../Projects/ProjectSmallBox';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import ArticleSkelton from '@buildwise/pages/project/components/ArticleSkelton';

const LinkProjectsModal = lazy(() => import('../Projects/LinkProjectsModal'));

type ActivityCardProps = {
  isLoggedInUser: boolean;
  user: UserType;
};

const ProfileActivity = ({ isLoggedInUser, user }: ActivityCardProps) => {
  const { company } = useUserData();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const { articles, isLoading } = useUserArticlesQuery({ username: user?.username });
  const [linkProjectsModal, setLinkProjectsModal] = useState(false);
  const { singularProjectsModuleName, pluralProjectsModuleName, projectsModule } =
    useBuildWiseModules();

  return (
    <ProfileActivityContainer>
      <Title variant="h5">Activity</Title>
      <StickyTabsContainer>
        <Tabs value={tab} onChange={(_: unknown, newValue: number) => setTab(newValue)}>
          <Tab
            icon={<FileListFillIcon size={18} />}
            iconPosition="start"
            key="posts"
            label={
              <>
                {articles.length === 1 ? 'Post' : 'Posts'}{' '}
                {articles.length > 0 && `(${articles.length})`}
              </>
            }
          />
          {!!projectsModule?.isActive && (
            <Tab
              icon={<BuildingFillIcon size={18} />}
              iconPosition="start"
              key="projects"
              label={
                <>
                  {user?.projects?.length === 1
                    ? singularProjectsModuleName
                    : pluralProjectsModuleName}{' '}
                  {user?.projects?.length > 0 && `(${user?.projects?.length})`}
                </>
              }
            />
          )}
        </Tabs>
      </StickyTabsContainer>
      <ListContainer>
        {tab === 0 && (
          <HandleOperation
            isLoading={isLoading}
            data={articles}
            noDataComponent={
              <NoDataContainer>
                <FileListLineIcon size={120} color={theme.palette.custom.steel} />
                <NoDataDescription>
                  {user?.userNameToDisplay} didn't publish any posts.
                </NoDataDescription>
              </NoDataContainer>
            }
            skelton={<ArticleSkelton variant="list" />}
          >
            {articles.map((article) => (
              <ArticleBox key={article.id} article={article} variant="list" doNotAutoplayVideos />
            ))}
          </HandleOperation>
        )}
        {tab === 1 && (
          <>
            {isLoggedInUser && (
              <Box justifyContent="flex-end" display="flex">
                <Button
                  sx={(theme) => ({
                    background: theme.palette.custom.white,
                  })}
                  startIcon={<CheckboxCircleFillIcon />}
                  variant="outlined"
                  onClick={() => setLinkProjectsModal(true)}
                >
                  Link {pluralProjectsModuleName}
                </Button>
              </Box>
            )}
            <HandleOperation
              data={user?.projects}
              noDataComponent={
                <NoDataContainer>
                  <BuildingLineIcon size={120} color={theme.palette.custom.steel} />
                  <NoDataDescription pb={4}>
                    {user?.userNameToDisplay} is not added to any of the{' '}
                    {singularProjectsModuleName.toLowerCase()} directories.
                  </NoDataDescription>
                </NoDataContainer>
              }
            >
              {user?.projects?.map((project) => (
                <ProjectSmallBox key={project.id} project={project} />
              ))}
            </HandleOperation>
          </>
        )}
      </ListContainer>

      <Suspense fallback={<Loader variant="fixed" />}>
        {linkProjectsModal && (
          <LinkProjectsModal
            linkProjectsModal={linkProjectsModal}
            setLinkProjectsModal={setLinkProjectsModal}
            projectsSelected={user?.projects || []}
            pluralProjectsModuleName={pluralProjectsModuleName}
          />
        )}
      </Suspense>
    </ProfileActivityContainer>
  );
};

const ProfileActivityContainer = styled(Paper, { label: 'ProfileActivityContainer' })(
  ({ theme }) => ({
    width: '100%',
    background: theme.palette.custom.white,
    padding: 24,
    borderRadius: 8,
    boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
    position: 'relative',
    marginBottom: 24,
  }),
);

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: '0.25px',
  color: theme.palette.secondary.dark,
}));

const ListContainer = styled(Box, { label: 'ListContainer' })(() => ({
  position: 'relative',
  minHeight: 200,
  marginTop: 8,
}));

const StickyTabsContainer = styled(Box, { label: 'StickyTabsContainer' })(({ theme }) => ({
  position: 'sticky',
  top: 79,
  padding: '16px 0',
  zIndex: 1,
  width: '100%',
  display: 'block',
  backgroundColor: theme.palette.custom.white,
}));

const NoDataContainer = styled(Box, { label: 'NoDataContainer' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  margin: '40px 24px 0',
});

const NoDataDescription = styled(Box, { label: 'NoDataDescription' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.mistBlue,
  maxWidth: '70%',
  textAlign: 'center',
}));

export default ProfileActivity;
